import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useBlog } from "../../context/BlogContext";
import BlogSection from "../../components/BlogSection";
import SEO from "../../components/SEO";

import { BLOG_SEO_OVERRIDES } from "../../services/blogSeoOverrides";
import { BLOG_IMAGE_ALT_MAP } from "../../services/blogImageAltMap";

const stripHtmlToText = (html = "") =>
  String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeComparableText = (value = "") =>
  String(value || "")
    .normalize("NFKD")
    .replace(/\u00a0/g, " ")
    .replace(/[’‘]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, " ")
    .replace(/[?!.,:;'"()[\]{}]/g, "")
    .trim()
    .toLowerCase();

const formatSlugToTitle = (slug = "") => {
  const cleanSlug = String(slug || "").trim();

  if (!cleanSlug) {
    return "Insurance Blog";
  }

  return cleanSlug
    .split("-")
    .filter(Boolean)
    .map(
      (word) =>
        word.charAt(0).toUpperCase() +
        word.slice(1),
    )
    .join(" ");
};

const formatBlogDate = (value = "") => {
  const cleanValue = String(value || "").trim();

  if (!cleanValue) {
    return "";
  }

  /*
   * Use only the YYYY-MM-DD part so browser timezone
   * does not change the displayed publication date.
   */
  const isoDateMatch = cleanValue.match(
    /^(\d{4})-(\d{2})-(\d{2})/,
  );

  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;

    const date = new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
      ),
    );

    return new Intl.DateTimeFormat("en-CA", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  }

  const parsedDate = new Date(cleanValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-CA", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(parsedDate);
};

/*
 * Remove WordPress classes that add text colours,
 * heading backgrounds or editor presentation styles.
 */
const removeWordPressPresentationClasses = (element) => {
  if (!element?.classList) {
    return;
  }

  Array.from(element.classList).forEach((className) => {
    const shouldRemove =
      className === "has-background" ||
      className === "has-text-color" ||
      className === "has-link-color" ||
      className === "has-inline-color" ||
      className.includes("-background-color") ||
      className.endsWith("-color");

    if (shouldRemove) {
      element.classList.remove(className);
    }
  });

  if (!element.className?.trim()) {
    element.removeAttribute("class");
  }
};

/*
 * Remove inline WordPress styles that interfere with
 * the blog design.
 */
const clearPresentationStyles = (
  element,
  {
    removeSpacing = false,
    removeTypography = false,
  } = {},
) => {
  if (!element) {
    return;
  }

  const styleProperties = [
    "background",
    "background-color",
    "background-image",
    "background-position",
    "background-repeat",
    "background-size",
    "color",
    "border-color",
    "box-shadow",
    "text-shadow",
  ];

  if (removeSpacing) {
    styleProperties.push(
      "margin",
      "margin-top",
      "margin-right",
      "margin-bottom",
      "margin-left",
      "padding",
      "padding-top",
      "padding-right",
      "padding-bottom",
      "padding-left",
    );
  }

  if (removeTypography) {
    styleProperties.push(
      "font-size",
      "font-family",
      "font-style",
      "font-weight",
      "line-height",
      "letter-spacing",
      "text-transform",
      "text-decoration",
    );
  }

  styleProperties.forEach((property) => {
    element.style?.removeProperty(property);
  });

  element.removeAttribute("bgcolor");
  element.removeAttribute("color");

  removeWordPressPresentationClasses(element);

  if (!element.getAttribute("style")?.trim()) {
    element.removeAttribute("style");
  }
};

const unwrapElement = (element) => {
  if (!element?.parentNode) {
    return;
  }

  element.replaceWith(
    ...Array.from(element.childNodes),
  );
};

/*
 * WordPress sometimes stores FAQ questions and small
 * headings as:
 *
 * <p><strong>Question?</strong></p>
 *
 * This function identifies those paragraphs and converts
 * them into proper H3 headings.
 */
const getCleanElementText = (element) =>
  String(element?.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isFaqSectionHeading = (element) => {
  const normalizedText = normalizeComparableText(
    element?.textContent || "",
  );

  return (
    normalizedText === "frequently asked questions" ||
    normalizedText === "frequently asked question" ||
    normalizedText === "faqs" ||
    normalizedText === "faq" ||
    normalizedText.includes(
      "frequently asked questions",
    )
  );
};

const paragraphCanBecomeHeading = (
  paragraph,
) => {
  if (!paragraph) {
    return false;
  }

  const text = getCleanElementText(paragraph);

  if (!text || text.length > 180) {
    return false;
  }

  if (
    paragraph.querySelector(
      "img, video, iframe, audio, picture, svg, ul, ol, table, blockquote",
    )
  ) {
    return false;
  }

  return true;
};

const replaceParagraphWithHeading = (
  documentNode,
  paragraph,
  className = "",
) => {
  const heading =
    documentNode.createElement("h3");

  heading.innerHTML = paragraph.innerHTML;

  if (className) {
    heading.classList.add(className);
  }

  paragraph.replaceWith(heading);

  return heading;
};

/*
 * FAQ questions are not consistent in WordPress.
 * Some are plain paragraphs, some are bold paragraphs,
 * and some are already heading tags.
 *
 * This function detects the FAQ section itself and
 * converts every question ending with "?" into a styled
 * H3 heading. It does not depend on the question already
 * being bold or blue.
 */
const convertFaqQuestions = (
  documentNode,
) => {
  const contentNodes = Array.from(
    documentNode.body.querySelectorAll(
      "h1, h2, h3, h4, h5, h6, p",
    ),
  );

  let insideFaqSection = false;
  let faqHeadingLevel = null;

  contentNodes.forEach((element) => {
    const headingMatch =
      element.tagName.match(/^H([1-6])$/);

    if (headingMatch) {
      const headingLevel = Number(
        headingMatch[1],
      );

      if (isFaqSectionHeading(element)) {
        insideFaqSection = true;
        faqHeadingLevel = headingLevel;
        return;
      }

      if (
        insideFaqSection &&
        faqHeadingLevel !== null &&
        headingLevel <= faqHeadingLevel
      ) {
        insideFaqSection = false;
        faqHeadingLevel = null;
        return;
      }

      if (
        insideFaqSection &&
        getCleanElementText(element).endsWith("?")
      ) {
        element.classList.add(
          "einsured-faq-question",
        );
      }

      return;
    }

    if (
      !insideFaqSection ||
      element.tagName !== "P"
    ) {
      return;
    }

    const questionText =
      getCleanElementText(element);

    if (
      paragraphCanBecomeHeading(element) &&
      questionText.endsWith("?")
    ) {
      replaceParagraphWithHeading(
        documentNode,
        element,
        "einsured-faq-question",
      );
    }
  });
};

/*
 * WordPress also stores some normal subheadings as:
 *
 * <p><strong>Heading text</strong></p>
 *
 * Convert only clearly heading-like bold paragraphs.
 * FAQ questions are handled separately above.
 */
const isHeadingLikeParagraph = (
  paragraph,
) => {
  if (!paragraphCanBecomeHeading(paragraph)) {
    return false;
  }

  const text = getCleanElementText(paragraph);

  const hasStrongElement = Boolean(
    paragraph.querySelector("strong, b"),
  );

  const visibleTextOutsideBoldElements =
    Array.from(paragraph.childNodes).some(
      (node) =>
        node.nodeType === 3 &&
        String(node.textContent || "")
          .replace(/\u00a0/g, " ")
          .trim(),
    );

  if (
    hasStrongElement &&
    !visibleTextOutsideBoldElements
  ) {
    return true;
  }

  const ownFontWeight =
    paragraph.style?.fontWeight || "";

  const numericFontWeight =
    Number.parseInt(ownFontWeight, 10);

  const hasBoldInlineStyle =
    ownFontWeight === "bold" ||
    (Number.isFinite(numericFontWeight) &&
      numericFontWeight >= 600);

  const hasBoldClass =
    paragraph.classList.contains("font-bold") ||
    paragraph.classList.contains(
      "font-semibold",
    );

  return (
    (hasBoldInlineStyle || hasBoldClass) &&
    (text.endsWith("?") ||
      text.endsWith(":") ||
      text.length <= 90)
  );
};

const convertHeadingLikeParagraphs = (
  documentNode,
) => {
  const paragraphs = Array.from(
    documentNode.querySelectorAll("p"),
  );

  paragraphs.forEach((paragraph) => {
    if (!isHeadingLikeParagraph(paragraph)) {
      return;
    }

    replaceParagraphWithHeading(
      documentNode,
      paragraph,
      "einsured-content-subheading",
    );
  });
};

const sanitizeHeading = (heading) => {
  if (!heading) {
    return;
  }

  clearPresentationStyles(heading, {
    removeSpacing: true,
    removeTypography: true,
  });

  /*
   * Remove colours and backgrounds from everything
   * nested inside the heading.
   */
  Array.from(
    heading.querySelectorAll("*"),
  ).forEach((child) => {
    clearPresentationStyles(child, {
      removeSpacing: false,
      removeTypography: true,
    });
  });

  /*
   * A WordPress <mark> tag can create a blue/yellow
   * highlight behind heading text. Remove the tag but
   * preserve its text.
   */
  Array.from(
    heading.querySelectorAll("mark"),
  ).forEach(unwrapElement);

  /*
   * Sometimes the background is applied to a wrapper
   * around the heading rather than the heading itself.
   */
  let wrapper = heading.parentElement;
  let level = 0;

  while (wrapper && level < 3) {
    const wrapperText =
      normalizeComparableText(
        wrapper.textContent || "",
      );

    const headingText =
      normalizeComparableText(
        heading.textContent || "",
      );

    const wrapperOnlyContainsHeading =
      wrapper.children.length === 1 &&
      wrapperText === headingText;

    if (!wrapperOnlyContainsHeading) {
      break;
    }

    clearPresentationStyles(wrapper, {
      removeSpacing: false,
      removeTypography: false,
    });

    wrapper = wrapper.parentElement;
    level += 1;
  }
};

function prepareBlogHtml(
  html = "",
  altMap = {},
  fallbackAlt = "",
  postTitle = "",
) {
  const input = String(html || "");

  if (!input) {
    return "";
  }

  try {
    const documentNode =
      new DOMParser().parseFromString(
        input,
        "text/html",
      );

    /*
     * Remove WordPress spacers because they create
     * large unwanted blank areas.
     */
    Array.from(
      documentNode.querySelectorAll(
        ".wp-block-spacer",
      ),
    ).forEach((spacer) => {
      spacer.remove();
    });

    /*
     * Remove empty paragraphs:
     *
     * <p></p>
     * <p>&nbsp;</p>
     * <p><br></p>
     */
    Array.from(
      documentNode.querySelectorAll("p"),
    ).forEach((paragraph) => {
      const paragraphText = String(
        paragraph.textContent || "",
      )
        .replace(/\u00a0/g, " ")
        .trim();

      const containsMedia =
        paragraph.querySelector(
          "img, video, iframe, audio, picture, svg",
        );

      if (
        !paragraphText &&
        !containsMedia
      ) {
        paragraph.remove();
      }
    });

    /*
     * Remove duplicate title received inside the
     * WordPress content.
     */
    const normalizedPostTitle =
      normalizeComparableText(postTitle);

    if (normalizedPostTitle) {
      const possibleTitleElements =
        Array.from(
          documentNode.querySelectorAll(
            "h1, h2, h3, h4, h5, h6, p",
          ),
        ).slice(0, 15);

      const duplicateTitleElement =
        possibleTitleElements.find(
          (element) => {
            const elementText =
              normalizeComparableText(
                element.textContent || "",
              );

            return (
              elementText &&
              elementText ===
                normalizedPostTitle
            );
          },
        );

      if (duplicateTitleElement) {
        duplicateTitleElement.remove();
      }
    }

    /*
     * Convert FAQ questions first. This works even when
     * WordPress stores the question as an unstyled <p>.
     */
    convertFaqQuestions(documentNode);

    /*
     * Convert other bold heading-like paragraphs into
     * consistent H3 subheadings.
     */
    convertHeadingLikeParagraphs(
      documentNode,
    );

    /*
     * Add alt text to WordPress content images.
     */
    Array.from(
      documentNode.querySelectorAll("img"),
    ).forEach((image) => {
      const src = String(
        image.getAttribute("src") || "",
      ).trim();

      const currentAlt = String(
        image.getAttribute("alt") || "",
      ).trim();

      if (!currentAlt) {
        const mappedAlt =
          src && altMap[src]
            ? String(altMap[src]).trim()
            : "";

        image.setAttribute(
          "alt",
          mappedAlt ||
            fallbackAlt ||
            "Blog image",
        );
      }

      image.removeAttribute("width");
      image.removeAttribute("height");
    });

    /*
     * Remove WordPress colour/background presentation
     * from text elements and common wrappers. Links are
     * styled again by the scoped CSS below.
     */
    Array.from(
      documentNode.body.querySelectorAll(
        "div, section, article, header, p, span, strong, b, em, i, u, mark, a, li, ul, ol, figure, figcaption, blockquote, h1, h2, h3, h4, h5, h6",
      ),
    ).forEach((element) => {
      clearPresentationStyles(element, {
        removeSpacing: false,
        removeTypography: false,
      });
    });

    /*
     * The React page title is the only H1.
     * Any H1 inside WordPress content becomes H2.
     */
    Array.from(
      documentNode.querySelectorAll("h1"),
    ).forEach((heading) => {
      const replacementHeading =
        documentNode.createElement("h2");

      replacementHeading.innerHTML =
        heading.innerHTML;

      Array.from(
        heading.attributes,
      ).forEach((attribute) => {
        replacementHeading.setAttribute(
          attribute.name,
          attribute.value,
        );
      });

      heading.replaceWith(
        replacementHeading,
      );
    });

    /*
     * Remove WordPress text colours, blue backgrounds,
     * padding and inline heading styling.
     */
    Array.from(
      documentNode.querySelectorAll(
        "h2, h3, h4, h5, h6",
      ),
    ).forEach(sanitizeHeading);

    /*
     * Remove inline spacing and typography from normal
     * content so React/Tailwind controls the design.
     */
    Array.from(
      documentNode.querySelectorAll(
        "p, ul, ol, li, figure, blockquote",
      ),
    ).forEach((element) => {
      clearPresentationStyles(element, {
        removeSpacing: true,
        removeTypography: true,
      });
    });

    return documentNode.body.innerHTML;
  } catch {
    return input;
  }
}

const INTERNAL_LINKS_BY_BLOG = {
  "super-visa-emergency-medical-insurance-the-key-to-a-worry-free-visit-for-your-parents-and-grandparents":
    {
      to: "/blog/not-able-to-pay-for-super-visa-insurance-in-full",
      text: "monthly payment options for Super Visa insurance",
    },

  "how-life-insurance-can-help-your-family-achieve-long-term-goals":
    {
      to: "/blog/how-to-use-whole-life-insurance-in-your-financial-planning",
      text: "whole life insurance in financial planning",
    },

  "why-disability-insurance-is-essential-protect-your-income-before-its-too-late":
    {
      to: "/disability-insurance",
      text: "disability insurance",
    },

  "how-to-use-whole-life-insurance-in-your-financial-planning":
    {
      to: "/blog/the-complete-guide-to-whole-life-insurance-benefits-in-canada",
      text: "whole life insurance benefits in Canada",
    },

  "my-term-life-insurance-is-expiring-now-what-are-my-options":
    {
      to: "/blog/what-options-do-you-have-when-your-term-life-insurance-is-expiring",
      text: "options when your term life insurance is expiring",
    },

  "critical-illness-insurance-everything-you-need-to-know":
    {
      to: "/critical-illness-insurance",
      text: "critical illness insurance",
    },

  "how-rrsp-in-canada-can-help-you-save-on-taxes":
    {
      to: "/blog/unlock-tax-savings-the-power-of-your-rrsp",
      text: "tax savings with RRSP",
    },

  "affordable-insurance-solutions-for-super-visa-applicants-in-canada":
    {
      to: "/blog/affordable-super-visa-insurance-without-compromising-coverage",
      text: "affordable Super Visa insurance without compromising coverage",
    },

  "top-factors-influencing-your-life-insurance-premium":
    {
      to: "/life-insurance",
      text: "life insurance",
    },

  "not-able-to-pay-for-super-visa-insurance-in-full":
    {
      to: "/super-visa-insurance",
      text: "Super Visa insurance",
    },

  "why-whole-life-insurance-may-not-be-the-right-choice-for-you-exploring-alternatives-in-canada":
    {
      to: "/whole-life-insurance",
      text: "whole life insurance",
    },

  "why-visitor-insurance-is-a-must-for-parents-visiting-international-students":
    {
      to: "/international-student-insurance",
      text: "international student insurance",
    },

  "how-to-choose-your-first-insurance-plan-and-supercharge-your-financial-portfolio":
    {
      to: "/life-insurance",
      text: "life insurance",
    },

  "5-things-we-learned-after-talking-to-hundreds-of-super-visa-clients":
    {
      to: "/super-visa-insurance",
      text: "Super Visa insurance",
    },

  "a-step-by-step-beginners-guide-to-buy-life-insurance":
    {
      to: "/blog/why-should-young-canadians-invest-in-life-insurance",
      text: "young Canadians investing in life insurance",
    },

  "why-private-medical-insurance-must-be-the-first-thing-on-your-mind-when-you-land-in-canada":
    {
      to: "/newcomers-insurance",
      text: "newcomers insurance",
    },

  "spotlight-on-critical-illness-insurance-is-it-worth-it":
    {
      to: "/critical-illness-insurance",
      text: "critical illness insurance",
    },

  "what-is-super-visa-insurance-and-how-much-does-it-cost":
    {
      to: "/blog/the-ultimate-super-visa-checklist",
      text: "ultimate Super Visa checklist",
    },

  "the-ultimate-guide-to-travel-insurance-for-your-canadian-adventure":
    {
      to: "/newcomers-insurance",
      text: "newcomers insurance",
    },

  "common-mistakes-to-avoid-when-buying-super-visa-insurance-in-canada":
    {
      to: "/blog/vital-insights-the-importance-of-maintaining-active-super-visa-insurance-throughout-your-canadian-stay",
      text: "maintaining active Super Visa insurance",
    },

  "why-buy-private-international-medical-insurance-when-studying-in-canada":
    {
      to: "/blog/international-students-financial-roadmap",
      text: "international students financial roadmap",
    },

  "key-benefits-of-term-life-insurance-for-young-families":
    {
      to: "/term-life-insurance",
      text: "term life insurance",
    },

  "why-women-need-to-prioritize-life-insurance":
    {
      to: "/life-insurance",
      text: "life insurance",
    },

  "mortgage-life-insurance-and-disability-insurance-what-are-they":
    {
      to: "/disability-insurance",
      text: "disability insurance",
    },

  "5-reliable-key-terms-on-how-to-buy-your-insurance-policy":
    {
      to: "/blog/tips-to-buy-life-insurance-online-the-smart-way",
      text: "tips to buy life insurance online",
    },

  "behind-the-scenes-a-day-in-the-life-of-an-insurance-advisor":
    {
      to: "/our-team",
      text: "our insurance team",
    },

  "private-mortgage-insurers-vs-banks-what-every-canadian-home-buyer-needs-to-know":
    {
      to: "/blog/understanding-the-difference-mortgage-insurance-vs-life-insurance-which-one-do-you-really-need",
      text: "mortgage insurance vs life insurance",
    },

  "life-insurance-101": {
    to: "/life-insurance",
    text: "life insurance",
  },

  "how-to-compare-life-insurance-to-get-the-most-for-yourself-and-family":
    {
      to: "/blog/key-benefits-of-term-life-insurance-for-young-families",
      text: "term life insurance for young families",
    },

  "the-ultimate-super-visa-checklist": {
    to: "/blog/common-mistakes-to-avoid-when-buying-super-visa-insurance-in-canada",
    text: "common Super Visa insurance mistakes",
  },

  "ebook-a-simple-guide-to-life-insurance": {
    to: "/life-insurance",
    text: "life insurance",
  },

  "international-students-financial-roadmap":
    {
      to: "/international-student-insurance",
      text: "international student insurance",
    },

  "why-should-young-canadians-invest-in-life-insurance":
    {
      to: "/blog/key-benefits-of-term-life-insurance-for-young-families",
      text: "term life insurance for young families",
    },

  "a-guide-to-health-insurance-in-canada-understanding-your-options":
    {
      to: "/health-insurance",
      text: "health insurance",
    },

  "term-life-insurance-in-canada-definition-advantages-and-factors-to-know-before-you-buy-a-plan":
    {
      to: "/term-life-insurance",
      text: "term life insurance",
    },

  "smart-savings-on-term-life-insurance-premiums-up-to-24-off":
    {
      to: "/term-life-insurance",
      text: "term life insurance",
    },

  "understanding-the-difference-mortgage-insurance-vs-life-insurance-which-one-do-you-really-need":
    {
      to: "/mortgage-insurance",
      text: "mortgage insurance",
    },

  "why-is-life-insurance-important-for-single-parents":
    {
      to: "/life-insurance",
      text: "life insurance",
    },

  "tips-to-buy-life-insurance-online-the-smart-way":
    {
      to: "/life-insurance-brampton",
      text: "life insurance in Brampton",
    },

  "registered-education-savings-plan-in-canada-a-simple-guide-to-help-you":
    {
      to: "/registered-education-savings-plan",
      text: "registered education savings plan",
    },

  "vital-insights-the-importance-of-maintaining-active-super-visa-insurance-throughout-your-canadian-stay":
    {
      to: "/super-visa-insurance",
      text: "Super Visa insurance",
    },

  "5-reasons-why-your-super-visa-application-in-brampton-can-be-denied":
    {
      to: "/super-visa-insurance",
      text: "Super Visa insurance",
    },

  "permanent-life-insurance-1": {
    to: "/permanent-life-insurance",
    text: "permanent life insurance",
  },

  "unlock-tax-savings-the-power-of-your-rrsp":
    {
      to: "/tax-free-savings-account",
      text: "tax-free savings account",
    },

  "the-complete-guide-to-whole-life-insurance-benefits-in-canada":
    {
      to: "/blog/how-to-use-whole-life-insurance-in-your-financial-planning",
      text: "whole life insurance in financial planning",
    },

  "a-comprehensive-guide-to-no-medical-insurance-for-beginners":
    {
      to: "/no-medical-insurance",
      text: "no medical insurance",
    },
};

const BLOG_POST_STYLES = `
  .einsured-blog-content {
    color: #202938;
    font-size: 18px;
    line-height: 1.62;
  }

  .einsured-blog-content p {
    margin: 0 0 20px !important;
    padding: 0 !important;
    color: #202938 !important;
    background: transparent !important;
    font-size: 18px !important;
    font-weight: 400 !important;
    line-height: 1.62 !important;
  }

  .einsured-blog-content h2,
  .einsured-blog-content h3,
  .einsured-blog-content h4,
  .einsured-blog-content h5,
  .einsured-blog-content h6 {
    display: block !important;
    width: 100% !important;
    padding: 0 !important;
    color: #0f172a !important;
    background: transparent !important;
    font-family: inherit !important;
    font-style: normal !important;
    text-transform: none !important;
    letter-spacing: normal !important;
    box-shadow: none !important;
    text-shadow: none !important;
  }

  .einsured-blog-content h2 {
    margin: 30px 0 14px !important;
    font-size: 28px !important;
    font-weight: 700 !important;
    line-height: 1.25 !important;
  }

  .einsured-blog-content h3 {
    margin: 24px 0 10px !important;
    font-size: 23px !important;
    font-weight: 700 !important;
    line-height: 1.3 !important;
  }

  .einsured-blog-content h4 {
    margin: 22px 0 9px !important;
    font-size: 21px !important;
    font-weight: 700 !important;
    line-height: 1.32 !important;
  }

  .einsured-blog-content h5 {
    margin: 20px 0 8px !important;
    font-size: 19px !important;
    font-weight: 700 !important;
    line-height: 1.35 !important;
  }

  .einsured-blog-content h6 {
    margin: 18px 0 8px !important;
    font-size: 18px !important;
    font-weight: 700 !important;
    line-height: 1.35 !important;
  }

  .einsured-blog-content h2:first-child,
  .einsured-blog-content h3:first-child,
  .einsured-blog-content h4:first-child {
    margin-top: 0 !important;
  }

  .einsured-blog-content h2 *,
  .einsured-blog-content h3 *,
  .einsured-blog-content h4 *,
  .einsured-blog-content h5 *,
  .einsured-blog-content h6 * {
    color: #0f172a !important;
    background: transparent !important;
    font-family: inherit !important;
    font-size: inherit !important;
    font-style: inherit !important;
    font-weight: inherit !important;
    line-height: inherit !important;
    text-decoration: none !important;
    box-shadow: none !important;
    text-shadow: none !important;
  }

  /*
   * FAQ questions are intentionally smaller than normal
   * H3 section headings, but remain bold and black.
   */
  .einsured-blog-content .einsured-faq-question {
    margin: 20px 0 5px !important;
    color: #0f172a !important;
    background: transparent !important;
    font-size: 19px !important;
    font-weight: 600 !important;
    line-height: 1.4 !important;
  }

  .einsured-blog-content .einsured-faq-question * {
    color: #0f172a !important;
    background: transparent !important;
    font-size: inherit !important;
    font-weight: inherit !important;
    line-height: inherit !important;
    text-decoration: none !important;
  }

  .einsured-blog-content .einsured-faq-question + p {
    margin-top: 0 !important;
  }

  .einsured-blog-content ul,
  .einsured-blog-content ol {
    margin: 16px 0 20px !important;
    padding-left: 28px !important;
  }

  .einsured-blog-content li {
    margin: 0 0 8px !important;
    color: #202938 !important;
    font-size: 18px !important;
    line-height: 1.58 !important;
  }

  .einsured-blog-content p a,
  .einsured-blog-content li a {
    color: #20a7df !important;
    background: transparent !important;
    font-weight: 600 !important;
    text-decoration: none !important;
  }

  .einsured-blog-content p a:hover,
  .einsured-blog-content li a:hover {
    text-decoration: underline !important;
  }

  .einsured-blog-content figure {
    margin: 24px 0 !important;
  }

  .einsured-blog-content img {
    max-width: 100% !important;
    height: auto !important;
  }

  .einsured-blog-content blockquote {
    margin: 24px 0 !important;
    padding: 14px 20px !important;
    border-left: 4px solid #20a7df !important;
    color: #374151 !important;
    background: #f7fbff !important;
  }

  @media (max-width: 640px) {
    .einsured-blog-content {
      font-size: 16px;
      line-height: 1.6;
    }

    .einsured-blog-content p,
    .einsured-blog-content li {
      font-size: 16px !important;
      line-height: 1.6 !important;
    }

    .einsured-blog-content h2 {
      margin-top: 26px !important;
      font-size: 24px !important;
    }

    .einsured-blog-content h3 {
      margin-top: 22px !important;
      font-size: 21px !important;
    }

    .einsured-blog-content h4 {
      font-size: 19px !important;
    }

    .einsured-blog-content .einsured-faq-question {
      margin-top: 18px !important;
      font-size: 17px !important;
    }
  }
`;

function BlogPostSkeleton() {
  return (
    <section className="bg-white py-10 sm:py-12 px-4 sm:px-6 md:px-12">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 rounded-full border-2 border-[#00B3FF] border-t-transparent animate-spin" />

          <p className="text-[#49628b] font-medium">
            Loading blog...
          </p>
        </div>

        <div className="mt-8 w-full h-[260px] sm:h-[440px] bg-[#eef4ff] rounded-md animate-pulse" />

        <div className="mt-4 flex items-center gap-3 animate-pulse">
          <div className="h-4 w-32 bg-[#eef4ff] rounded-md" />
          <div className="h-4 w-2 bg-[#eef4ff] rounded-md" />
          <div className="h-4 w-32 bg-[#eef4ff] rounded-md" />
        </div>

        <div className="mt-8 animate-pulse">
          <div className="h-10 sm:h-12 w-[92%] bg-[#eef4ff] rounded-xl" />
          <div className="mt-3 h-10 sm:h-12 w-[70%] bg-[#eef4ff] rounded-xl" />
        </div>

        <div className="mt-8 space-y-4 animate-pulse">
          <div className="h-4 w-[96%] bg-[#eef4ff] rounded-lg" />
          <div className="h-4 w-[90%] bg-[#eef4ff] rounded-lg" />
          <div className="h-4 w-[94%] bg-[#eef4ff] rounded-lg" />
          <div className="h-4 w-[82%] bg-[#eef4ff] rounded-lg" />
        </div>
      </div>
    </section>
  );
}

export default function BlogPost() {
  const { name: rawName } = useParams();
  const name = String(rawName || "").trim();

  const { getPost, posts } = useBlog();

  const [post, setPost] = useState(null);
  const [loading, setLoading] =
    useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadPost = async () => {
      try {
        setErr("");
        setLoading(true);
        setPost(null);

        const postData = await getPost(name);

        if (mounted) {
          setPost(postData);
        }
      } catch (error) {
        if (mounted) {
          setErr(
            error?.message ||
              "Failed to load blog.",
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadPost();

    return () => {
      mounted = false;
    };
  }, [name, getPost]);

  const { prevName, nextName } =
    useMemo(() => {
      const sortedPosts =
        Array.isArray(posts)
          ? [...posts]
          : [];

      sortedPosts.sort(
        (firstPost, secondPost) =>
          (
            secondPost?.date || ""
          ).localeCompare(
            firstPost?.date || "",
          ),
      );

      const currentIndex =
        sortedPosts.findIndex(
          (currentPost) =>
            currentPost?.name === name,
        );

      return {
        nextName:
          currentIndex > 0
            ? sortedPosts[
                currentIndex - 1
              ]?.name
            : null,

        prevName:
          currentIndex >= 0 &&
          currentIndex <
            sortedPosts.length - 1
            ? sortedPosts[
                currentIndex + 1
              ]?.name
            : null,
      };
    }, [posts, name]);

  const override =
    BLOG_SEO_OVERRIDES[name];

  const apiTitle = post?.metaTitle
    ? String(post.metaTitle).trim()
    : "";

  const apiDescription =
    post?.metaDesc
      ? String(post.metaDesc).trim()
      : "";

  const plainTextExcerpt =
    post?.html
      ? stripHtmlToText(post.html).slice(
          0,
          160,
        )
      : "";

  const fallbackTitleFromSlug =
    formatSlugToTitle(name);

  const seoTitle =
    override?.title ||
    apiTitle ||
    post?.title ||
    `${fallbackTitleFromSlug} | einsured.ca`;

  const seoDescription =
    override?.description ||
    apiDescription ||
    plainTextExcerpt ||
    "Read insurance tips, guides, and updates from einsured.ca.";

  const seoKeywords =
    override?.keyphrase || "";

  const featuredAlt =
    (post?.featuredImage &&
      BLOG_IMAGE_ALT_MAP[
        post.featuredImage
      ]) ||
    post?.title ||
    "Blog featured image";

  const preparedHtml = post
    ? prepareBlogHtml(
        post?.html || "",
        BLOG_IMAGE_ALT_MAP,
        post?.title || "",
        post?.title || "",
      )
    : "";

  const authorName =
    typeof post?.authorName ===
      "string" &&
    post.authorName.trim()
      ? post.authorName.trim()
      : "einsured.ca Team";

  const publishedDate =
    formatBlogDate(post?.date);

  const internalLink =
    INTERNAL_LINKS_BY_BLOG[name];

  if (loading) {
    return (
      <>
        <SEO
          title={seoTitle}
          description={seoDescription}
          keywords={seoKeywords}
          ogTitle={seoTitle}
          ogDescription={seoDescription}
          ogType="article"
          ogImage={
            post?.featuredImage || ""
          }
        />

        <BlogPostSkeleton />
      </>
    );
  }

  if (err) {
    return (
      <>
        <SEO
          title={seoTitle}
          description={seoDescription}
          keywords={seoKeywords}
          ogTitle={seoTitle}
          ogDescription={seoDescription}
          ogType="article"
          ogImage={
            post?.featuredImage || ""
          }
        />

        <div className="min-h-[60vh] bg-white p-10 text-red-600">
          {err}
        </div>
      </>
    );
  }

  if (!post) {
    return (
      <>
        <SEO
          title={seoTitle}
          description={seoDescription}
          keywords={seoKeywords}
          ogTitle={seoTitle}
          ogDescription={seoDescription}
          ogType="article"
        />

        <div className="min-h-[60vh] bg-white p-10 text-[#49628b]">
          Blog post not found.
        </div>
      </>
    );
  }

  const blogContentClasses =
    "einsured-blog-content blog-content mt-8 max-w-none";

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        ogTitle={seoTitle}
        ogDescription={seoDescription}
        ogType="article"
        ogImage={
          post?.featuredImage || ""
        }
      />

      <style>{BLOG_POST_STYLES}</style>

      <section className="bg-white py-10 sm:py-12 px-4 sm:px-6 md:px-12">
        <article className="max-w-[1200px] mx-auto">
          {post.featuredImage ? (
            <img
              src={post.featuredImage}
              alt={featuredAlt}
              className="block w-full h-auto object-cover"
              loading="eager"
              fetchPriority="high"
            />
          ) : null}

          <div
            className={`${
              post.featuredImage
                ? "mt-4"
                : ""
            } flex flex-wrap items-center text-[15px] sm:text-[17px] font-medium text-[#20a7df]`}
          >
            <span>
              By {authorName}
            </span>

            {publishedDate ? (
              <>
                <span
                  className="mx-2 text-[#6b7280]"
                  aria-hidden="true"
                >
                  |
                </span>

                <time
                  dateTime={
                    post?.date ||
                    undefined
                  }
                >
                  {publishedDate}
                </time>
              </>
            ) : null}
          </div>

          <h1 className="mt-8 mb-0 text-[30px] sm:text-[38px] lg:text-[42px] font-bold text-[#0f172a] leading-[1.18]">
            {post.title}
          </h1>

          <div
            className={blogContentClasses}
            dangerouslySetInnerHTML={{
              __html: preparedHtml,
            }}
          />

          {internalLink ? (
            <div className="mt-8 rounded-xl border border-[#dbe7f7] bg-[#f7fbff] p-5 text-[#49628b]">
              <p className="m-0 text-base sm:text-lg leading-7">
                Related reading:{" "}
                <Link
                  to={internalLink.to}
                  className="text-[#028fdc] font-semibold underline underline-offset-4 hover:text-[#49628b]"
                >
                  {internalLink.text}
                </Link>
              </p>
            </div>
          ) : null}

          <div className="mt-10 flex items-center justify-between gap-4 border-t pt-8">
            {prevName ? (
              <Link
                className="text-[#00B3FF] font-semibold hover:underline"
                to={`/blog/${prevName}`}
              >
                ← Previous Post
              </Link>
            ) : (
              <span />
            )}

            {nextName ? (
              <Link
                className="text-[#00B3FF] font-semibold text-right hover:underline"
                to={`/blog/${nextName}`}
              >
                Next Post →
              </Link>
            ) : (
              <span />
            )}
          </div>
        </article>
      </section>

      <BlogSection
        mode="related"
        relatedCount={3}
        excludeName={name}
        title="Related Blogs"
      />
    </>
  );
}